import type { PriceLedgerEntryDto } from '../../services/integrationApi';

/** The ledger table's segmented filter: every row, rows written, rows a guard refused or a write failed. */
export type LedgerFilter = 'all' | 'written' | 'refused';

export function ledgerCounts(entries: PriceLedgerEntryDto[]): { all: number; written: number; refused: number } {
  const written = entries.filter((e) => e.outcome === 'written').length;
  return { all: entries.length, written, refused: entries.length - written };
}

/**
 * Suffixes of `integrations.priceLedger.*`. A plain string makes
 * `` t(`integrations.priceLedger.${label}`) `` a `${string}` template, which the
 * CRA build rejects (TS2345). These unions keep that call a known key.
 */
type PriceLedgerLabelKey =
  | 'outcomeWritten'
  | 'outcomeFailed'
  | 'outcomeListingMismatch'
  | 'outcomeListingReview'
  | 'outcomePriceDrop'
  | 'outcomePriceJump'
  | 'outcomeBelowCost'
  | 'outcomeStorePrice'
  | 'outcomeBelowMap';

type PriceLedgerHintKey =
  | 'outcomeHintListingMismatch'
  | 'outcomeHintListingReview'
  | 'outcomeHintPriceDrop'
  | 'outcomeHintPriceJump'
  | 'outcomeHintBelowCost'
  | 'outcomeHintStorePrice'
  | 'outcomeHintBelowMap';

/** i18n keys under `integrations.priceLedger` for a ledger outcome. Unknown codes stay raw-but-readable. */
const LEDGER_OUTCOME_KEYS: Record<string, { label: PriceLedgerLabelKey; hint: PriceLedgerHintKey | '' }> = {
  written: { label: 'outcomeWritten', hint: '' },
  write_failed: { label: 'outcomeFailed', hint: '' },
  identity_mismatch: { label: 'outcomeListingMismatch', hint: 'outcomeHintListingMismatch' },
  identity_review: { label: 'outcomeListingReview', hint: 'outcomeHintListingReview' },
  price_collapse_guard: { label: 'outcomePriceDrop', hint: 'outcomeHintPriceDrop' },
  price_jump_guard: { label: 'outcomePriceJump', hint: 'outcomeHintPriceJump' },
  price_below_cost_guard: { label: 'outcomeBelowCost', hint: 'outcomeHintBelowCost' },
  price_changed_in_shopify: { label: 'outcomeStorePrice', hint: 'outcomeHintStorePrice' },
  below_map: { label: 'outcomeBelowMap', hint: 'outcomeHintBelowMap' },
};

export function ledgerOutcomeKeys(outcome: string): { label: PriceLedgerLabelKey; hint: PriceLedgerHintKey | '' } | null {
  return LEDGER_OUTCOME_KEYS[outcome] ?? null;
}

/** A code the ledger does not name yet, shown as words rather than snake_case. */
export function humanizeLedgerOutcome(outcome: string): string {
  const words = outcome.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : outcome;
}

/** Rows for a filter and a search over product name, SKU, Motzu product id and Shopify variant id. */
export function filterLedgerEntries(entries: PriceLedgerEntryDto[], filter: LedgerFilter, search: string): PriceLedgerEntryDto[] {
  const needle = search.trim().toLowerCase();
  return entries.filter((entry) => {
    if (filter === 'written' && entry.outcome !== 'written') return false;
    if (filter === 'refused' && entry.outcome === 'written') return false;
    if (!needle) return true;
    return [entry.productName, entry.productSku, entry.productId, entry.variantId]
      .some((value) => String(value || '').toLowerCase().includes(needle));
  });
}
